import React, { useContext, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { productsData } from "../ProductsData";
import { CartContext } from "../components/CartContext";
import { useWishlist } from "../components/WishlistContext";

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const query = (searchParams.get("q") || "").toLowerCase().trim();

  const { addToCart } = useContext(CartContext);
  const { wishlistItems, toggleWishlist } = useWishlist();
  const [alert, setAlert] = useState({ visible: false, message: "", type: "" });

  const searchPerformed = query.length > 0;

  const allProducts = useMemo(() => {
    let products = [];
    Object.entries(productsData).forEach(([categoryName, catVal]) => {
      if (Array.isArray(catVal)) {
        products.push(...catVal.map(p => ({ ...p, category: categoryName })));
      } else if (typeof catVal === "object" && catVal !== null) {
        Object.entries(catVal).forEach(([subCategoryName, arr]) => {
          if (Array.isArray(arr)) {
            products.push(...arr.map(p => ({ ...p, category: categoryName, subcategory: subCategoryName })));
          }
        });
      }
    });
    return products;
  }, []);

  const filteredProducts = useMemo(() => {
    if (!searchPerformed) return [];

    const matchingCategoryOrSub = Object.entries(productsData).find(([cat, catVal]) => {
      if (cat.toLowerCase() === query) return true;
      if (typeof catVal === "object" && catVal !== null) {
        return Object.keys(catVal).some(sub => sub.toLowerCase() === query);
      }
      return false;
    });

    if (matchingCategoryOrSub) {
      const [catName, catVal] = matchingCategoryOrSub;
      let productsInCat = [];
      if (Array.isArray(catVal)) {
        productsInCat.push(...catVal.map(p => ({ ...p, category: catName })));
      } else {
        Object.entries(catVal).forEach(([subName, arr]) => {
          if (Array.isArray(arr)) {
            productsInCat.push(...arr.map(p => ({ ...p, category: catName, subcategory: subName })));
          }
        });
      }
      return productsInCat.filter(p =>
        p.category.toLowerCase() === query ||
        (p.subcategory && p.subcategory.toLowerCase() === query) ||
        p.name.toLowerCase().includes(query) ||
        p.description.toLowerCase().includes(query)
      );
    }

    return allProducts.filter(
      p =>
        p.name.toLowerCase().includes(query) ||
        p.description.toLowerCase().includes(query)
    );
  }, [allProducts, query, searchPerformed]);

  const handleAddToCart = (product) => {
    addToCart(product);
    setAlert({ visible: true, message: `${product.name.slice(0, 30)}... added to cart!`, type: "success" });
    setTimeout(() => setAlert({ visible: false, message: "", type: "" }), 3000);
  };

  const selectedCurrency = "USD"; // default currency

  return (
    <div className="container mb-5">
      <nav aria-label="breadcrumb" className="mt-4 mb-5">
        <ol className="breadcrumb">
          {/* Home Link */}
          <li className="breadcrumb-item">
            <Link to="/" className="text-dark text-decoration-none">
              <i className="fas fa-home me-2"></i> Home
            </Link>
          </li>
          <li className="breadcrumb-item">
            <i className="fa-regular fa-folder me-2"></i> Search : {query}
          </li>
        </ol>
      </nav>
      {alert.visible && (
        <div className={`overlay-alert ${alert.type}`}>
          <div className="alert-content d-flex align-items-center">
            <i className="fa-solid fa-check me-2"></i>
            <strong>Success! </strong> {alert.message}
            <button
              type="button"
              className="btn-close ms-auto"
              onClick={() => setAlert({ visible: false, message: "", type: "" })}
            ></button>
          </div>
          <div className="progress-bar"></div>
        </div>
      )}

      {!searchPerformed ? (
        <div className="text-center p-5 bg-light rounded">
          <p className="lead text-muted">Enter a product name, category, or subcategory to start searching.</p>
        </div>
      ) : filteredProducts.length > 0 ? (
        <div className="row g-4">
          {filteredProducts.map((product) => {
            const { id, name, image, price, oldPrice, category, subcategory, description } = product;
            const productLink = subcategory
              ? `/product/${category}/${subcategory}/${id}`
              : `/product/${category}/${id}`;
            const isWishlisted = wishlistItems.some(item => item.id === id);
            const discountPercent = oldPrice ? Math.round(((oldPrice - price) / oldPrice) * 100) : 0;

            return (
              <div key={id} className="col-12 col-sm-6 col-md-4 col-lg-3">
                <div className="card h-100 border-0 rounded-3 overflow-hidden shadow-sm product-card-hover">
                  <div className="position-absolute top-0 start-0 m-3">
                    <span className="badge bg-dark text-white px-3 py-2 rounded-pill">
                      Best Seller
                    </span>
                  </div>

                  <button
                    className="btn btn-light position-absolute top-0 end-0 m-3 rounded-circle shadow-sm"
                    onClick={(e) => { e.stopPropagation(); toggleWishlist(product); }}
                    style={{ width: '40px', height: '40px', padding: 0, zIndex: 999 }}
                  >
                    <i className={`fa-heart fa-sm ${isWishlisted ? 'fa-solid text-danger' : 'fa-regular'}`}></i>
                  </button>

                  <div className="product-image-container">
                    <Link to={productLink} className="d-block w-100 h-100">
                      <img src={image} className="product-image" alt={name} />
                    </Link>
                  </div>

                  <div className="card-body p-4">
                    <p className="text-uppercase text-muted small mb-1 fw-bold">
                      {subcategory ? subcategory : category}
                    </p>

                    <Link to={productLink} className="text-decoration-none text-dark">
                      <h5 className="card-title fw-bold mb-2" style={{ fontSize: '1.1rem', lineHeight: '1.3' }}>
                        {name.slice(0, 30)}...
                      </h5>
                    </Link>

                    <p className="text-dark small mb-4" style={{ lineHeight: '1.4', fontSize: '0.85rem' }}>
                      {description.slice(0, 60)}...
                    </p>

                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <div className="d-flex align-items-baseline">
                        <span className="fw-bold fs-4 text-dark">
                          {selectedCurrency === "INR" ? `₹${price.toFixed(0)}` : `$${price.toFixed(0)}`}
                        </span>
                        {oldPrice && (
                          <span className="text-muted text-decoration-line-through ms-2 small">
                            {selectedCurrency === "INR" ? `₹${oldPrice.toFixed(0)}` : `$${oldPrice.toFixed(0)}`}
                          </span>
                        )}
                      </div>

                      <button
                        className="btn btn-dark rounded-pill px-4 py-2 fw-bold buy-now-btn"
                        onClick={() => handleAddToCart(product)}
                        style={{ fontSize: '0.9rem', transition: 'all 0.3s ease' }}
                      >
                        Add to Cart <i className="fa-solid fa-arrow-up-right-from-square"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-center text-muted mt-4">No products found.</p>
      )}
    </div>
  );
};

export default SearchPage;
